import { buildCustomerDetailPath } from './customer-navigation';
import { buildOrdersListPath, type OrderStatusFilter } from './order-status-filters';

export interface OrderReturnNavigation {
  path: string;
  label: string;
}

export interface OrderLocationState {
  returnTo?: OrderReturnNavigation;
  /** Back target for order detail when opened from timeline (preserves filtered list context). */
  detailReturnTo?: OrderReturnNavigation;
  /** @deprecated Prefer returnTo */
  from?: 'dashboard' | 'customer';
  /** @deprecated Prefer returnTo */
  customerId?: number;
  /** @deprecated Prefer returnTo */
  customerEmail?: string;
}

export function orderListReturn(statusFilter: OrderStatusFilter = 'all'): OrderReturnNavigation {
  return { path: buildOrdersListPath(statusFilter), label: 'Back to Orders' };
}

export function orderDetailReturn(orderId: number): OrderReturnNavigation {
  return { path: `/orders/${orderId}`, label: 'Back to Order' };
}

export function customerDetailReturn(customerId: number, email?: string): OrderReturnNavigation {
  return {
    path: buildCustomerDetailPath(customerId, email),
    label: 'Back to Customer',
  };
}

export function dashboardReturn(path: string = '/analytics'): OrderReturnNavigation {
  return { path, label: 'Back to Dashboard' };
}

export function buildOrderLocationState(returnTo: OrderReturnNavigation): OrderLocationState {
  return { returnTo };
}

/** State when opening timeline from order list — preserves order-detail back target. */
export function buildTimelineLocationState(
  orderId: number,
  detailReturnTo?: OrderReturnNavigation,
): OrderLocationState {
  const state: OrderLocationState = { returnTo: orderDetailReturn(orderId) };
  if (detailReturnTo) {
    state.detailReturnTo = detailReturnTo;
  }
  return state;
}

/** Router state to pass when navigating back from timeline to order detail. */
export function resolveOrderDetailNavigationState(
  state: unknown,
): OrderLocationState | undefined {
  const locationState = state as OrderLocationState | null | undefined;
  if (locationState?.detailReturnTo) {
    return buildOrderLocationState(locationState.detailReturnTo);
  }
  return undefined;
}

function resolveLegacyReturn(state: OrderLocationState | null | undefined): OrderReturnNavigation | null {
  if (state?.returnTo?.path && state.returnTo.label) {
    return state.returnTo;
  }
  if (state?.from === 'dashboard') {
    return dashboardReturn();
  }
  if (state?.from === 'customer' && state.customerId) {
    return customerDetailReturn(state.customerId, state.customerEmail);
  }
  return null;
}

/** Back target for Order detail — defaults to the orders list. */
export function resolveOrderDetailBack(state: unknown): OrderReturnNavigation {
  return resolveLegacyReturn(state as OrderLocationState) ?? orderListReturn();
}

/** Back target for Order timeline — defaults to the parent order detail. */
export function resolveOrderTimelineBack(state: unknown, orderId: number): OrderReturnNavigation {
  return resolveLegacyReturn(state as OrderLocationState) ?? orderDetailReturn(orderId);
}

export function formatOrderBackLabel(label: string): string {
  return `← ${label}`;
}
